import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { syncExistingWebPushAssociation, unregisterRuntimePushToken } from "../lib/pushClient";
import { applyTheme } from "../lib/theme";

const AuthContext = createContext(null);

function applyPreferences(nextUser) {
  if (!nextUser) return;
  if (nextUser.theme) {
    const theme = applyTheme(nextUser.theme);
    window.dispatchEvent(new CustomEvent("pc-theme-change", { detail: theme }));
  }
  if (nextUser.locale) {
    localStorage.setItem("pc_lang", nextUser.locale);
    window.dispatchEvent(new CustomEvent("pc-language-change", { detail: nextUser.locale }));
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyPreferences(data.user);
      setUser(data.user);
    } catch {
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (["player", "admin"].includes(user?.role)) syncExistingWebPushAssociation().catch(() => {});
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyPreferences(data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    await unregisterRuntimePushToken();
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, setUser, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
